import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { addDays } from 'date-fns'
import { Repeat } from 'lucide-react'
import { formatDateToISO, getTodayDateString, getWeekStartDate } from '../utils/noteDateUtils'
import { useCycles, usePersonalNotes, useProjects } from '@/lib/db/rxdb/hooks'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import useLoginStore from '@/store/LoginStore'
import { useTimezone } from '@/store/UserPreferencesStore'
import { cn } from '@/lib/utils'

/**
 * The top of a weekly note (design 10b): this week's daily notes, linked —
 * days without a note are dashed — and any cycles running this week.
 */
export function WeekStrip({ weekKey }: { weekKey: string }) {
  const { t } = useTranslation('notes')
  const { date: formatDate } = useDateTimeFormatters()
  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)
  const workspaceId = useLoginStore((s) => s.workspaceId)
  const timezone = useTimezone()
  const { notes } = usePersonalNotes('daily')
  const { cycles } = useCycles()
  const { projects } = useProjects({ isDeleted: false })

  const days = useMemo(() => {
    const start = getWeekStartDate(weekKey)
    if (!start) return []
    return Array.from({ length: 7 }, (_, i) => addDays(start, i))
  }, [weekKey])

  const noteDates = useMemo(() => new Set(notes.map((n) => n.noteDate)), [notes])
  const today = getTodayDateString(timezone)

  const weekCycles = useMemo(() => {
    const first = days[0]
    const last = days.at(-1)
    if (!first || !last) return []
    const from = first.getTime()
    const to = addDays(last, 1).getTime()
    const projectName = new Map(projects.map((p) => [p.id, p.name]))
    return cycles
      .filter(
        (c) =>
          !c.isDeleted &&
          c.status !== 'completed' &&
          (!workspaceId || c.envId === workspaceId) &&
          c.startDate < to &&
          c.endDate >= from,
      )
      .map((c) => ({ ...c, projectName: projectName.get(c.projectId) }))
  }, [cycles, days, projects, workspaceId])

  if (days.length === 0) return null

  return (
    <section className="mb-6 space-y-3">
      <div className="grid grid-cols-7 gap-1.5">
        {days.map((day) => {
          const key = formatDateToISO(day)
          const hasNote = noteDates.has(key)
          const label = (
            <>
              <span className="text-[10.5px] font-medium uppercase">{formatDate(day, { weekday: 'narrow' })}</span>
              <span className="text-[15px] font-semibold tabular-nums">{day.getDate()}</span>
            </>
          )
          // Days after today can't be opened — no writing ahead.
          if (key > today) {
            return (
              <span
                key={key}
                className="flex flex-col items-center rounded-lg border border-dashed py-2 text-center text-muted-foreground/50"
              >
                {label}
              </span>
            )
          }
          return (
            <Link
              key={key}
              to="/$workspaceSlug/notes/daily/$noteDate"
              params={{ workspaceSlug, noteDate: key }}
              className={cn(
                'flex flex-col items-center rounded-lg py-2 text-center transition-colors',
                hasNote ? 'border bg-card hover:bg-secondary/50' : 'border border-dashed text-muted-foreground hover:bg-secondary/40',
                key === today && 'ring-1 ring-brand',
              )}
            >
              {label}
            </Link>
          )
        })}
      </div>
      <p className="text-[11px] text-muted-foreground">{t('weekStripHint')}</p>
      {weekCycles.map((cycle) => (
        <div key={cycle.id} className="flex items-center gap-2 rounded-lg bg-secondary/50 px-3 py-2 text-[13px]">
          <Repeat className="size-3.5 text-muted-foreground" />
          <span className="font-medium">{cycle.name}</span>
          {cycle.projectName && <span className="text-muted-foreground">· {cycle.projectName}</span>}
          <span className="ml-auto text-xs text-muted-foreground">
            {cycle.status === 'active'
              ? t('cycleActive')
              : t('cycleStarts', { date: formatDate(new Date(cycle.startDate), { weekday: 'short' }) })}
          </span>
        </div>
      ))}
    </section>
  )
}
